import currency from 'currency.js'
import axiosInstance from '@/axios'
import { salesPriceFormatNumber } from '@/utils/currency'
import { getImageBaseUrl } from '@/utils/imageBaseUrl'
import { describeError } from "@/utils/safeErrorLog";

/**
 * Is this a usable server-resolved price in whole cents?
 *
 * 0 is USABLE -- a genuinely free item is a real price, and treating it as
 * missing would silently quote the product default over a deliberate zero.
 * Only null / undefined / non-finite / negative are rejected.
 */
function centsAreUsable (cents) {
  return (
    cents !== null &&
    cents !== undefined &&
    Number.isFinite(Number(cents)) &&
    Number(cents) >= 0
  )
}

/**
 * Whole cents -> a dollars NUMBER, rounded to the cent.
 *
 * A NUMBER, not a string, because every caller of
 * ``computeProductVariantPrice`` does arithmetic on the result:
 * ``price * cartItem.quantity`` (StorefrontCart) and
 * ``price.toFixed(5)`` (StorefrontCart:913, Order/Cart). On a string the
 * multiply coerces silently and the ``.toFixed`` calls throw outright.
 * Returning a number is the only type that is safe at every call site.
 *
 * The ``toFixed(2)`` + ``Number`` round-trip pins the value to the cent
 * before any caller multiplies it, so a quantity multiply cannot smear a
 * float artefact across a line total.
 */
function centsToDollars (cents) {
  return Number((Number(cents) / 100).toFixed(2))
}

/**
 * One (primary, secondary) read against a ``variant_prices`` map.
 *
 * Returns the cents, or ``undefined`` for "not in this map" -- distinct
 * from the 0 that a genuinely free variant returns, which is why this
 * signals absence with ``undefined`` rather than a falsy number.
 *
 * Own-property reads only. A bare ``map[primary]`` walks the prototype
 * chain, so a colour or size literally named "constructor" or "toString"
 * would return a Function and be treated as a row -- a shopper-supplied
 * value must never reach a prototype member.
 */
function readVariantCents (map, primary, secondary) {
  if (primary === null || primary === undefined) {
    return undefined
  }
  if (secondary === null || secondary === undefined) {
    return undefined
  }
  if (!Object.prototype.hasOwnProperty.call(map, primary)) {
    return undefined
  }
  const row = map[primary]
  if (!row || typeof row !== 'object') {
    return undefined
  }
  if (!Object.prototype.hasOwnProperty.call(row, secondary)) {
    return undefined
  }
  const cents = row[secondary]
  return centsAreUsable(cents) ? cents : undefined
}

export default {
  methods: {

    roundTo (n, digits) {
      var negative = false
      if (digits === undefined) {
        digits = 0
      }
      if (n < 0) {
        negative = true
        n = n * -1
      }
      var multiplicator = Math.pow(10, digits)
      n = parseFloat((n * multiplicator).toFixed(11))
      n = (Math.round(n) / multiplicator).toFixed(digits)
      if (negative) {
        n = (n * -1).toFixed(digits)
      }
      return n
    },

    isMobile: state => {
      var check = false;
      (function (a) {
        if (
                    /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
                        a
                    ) ||
                    /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
                        a.substr(0, 4)
                    )
                ) { check = true }
      })(navigator.userAgent || navigator.vendor || window.opera)
      return check
    },

    /**
     * @deprecated Use `formatCurrencyNumber` (bare) or `formatCurrency` (with $)
     * from `@/utils/currency` directly. This mixin method remains as a thin
     * wrapper for legacy callsites; the canonical implementation lives in
     * `frontend/src/utils/currency.js`. Mixin removal is a P2 follow-up after
     * the callsite audit completes (sweep landed 2026-05-18).
     *
     * NOTE: this wrapper deliberately preserves the legacy NO-THOUSANDS-SEPARATOR
     * format ("1234.50", not "1,234.50") because some callsites bind the result
     * back into a `<v-text-field type="number">` v-model which cannot accept
     * commas. The canonical `formatCurrencyNumber` IS thousands-aware — use it
     * for display-only sites where the result lands in markup, not in a
     * `type="number"` input.
     */
    formatCurrency (val) {
      // Round to 2dp via the canonical parser → number path, then bare toFixed.
      // Preserves the pre-sweep "1234.50" / "0.00" return contract.
      if (val === null || val === undefined) return '0.00'
      const n = Number(val)
      if (!Number.isFinite(n)) return '0.00'
      return (Math.round(n * 100) / 100).toFixed(2)
    },

    getImage (uuid) {
      const imageBaseUrl = getImageBaseUrl()
      if (uuid) {
        // Product-image picker slice 1: storefront_images stores already
        // -resolved absolute URLs (SanMar CDN / Artifi-served), NOT S3
        // uuids. Pass those through unchanged so the same call site can
        // render both a legacy uuid and a resolved picker URL.
        if (/^https?:\/\//i.test(uuid)) {
          return uuid
        }
        return `${imageBaseUrl}${uuid}`
      }

      return `${imageBaseUrl}default-product.svg`
    },

    isNumber (value) {
      if ((undefined === value) || (value === null)) {
        return false
      }
      if (typeof value === 'number') {
        return true
      }
      return !isNaN(value - 0)
    },

    /**
     * The price to QUOTE for one (colour, size) of a campaign product.
     *
     * This is the choke point for every variant price the UI shows: both
     * storefront carts, the storefront product card and quickview, the
     * four Order dialogs, and the store-editor product card all call it.
     *
     * IT IS A LOOKUP, NOT A CALCULATION -- and that is load-bearing.
     * The price comes from the SERVER, which resolves it through
     * ``compute_order_item_price_snapshot``, the one function that writes
     * ``order_items.unit_price`` at checkout. Computing it here instead
     * would mean reimplementing ``margin_resolver``'s four-level
     * hierarchy (per-size override -> store pricing grid -> distributor
     * default -> SA global -> last-resort) along with quantity breaks and
     * grid-mode conversion. FOUR server-side reimplementations of that
     * chain already exist for display purposes; a JavaScript fifth would
     * drift from the charge, which is the very defect this fixes.
     * ``added_cost_and_margin`` IS present on the getCampaignProductById
     * payload -- that is a temptation, not a licence. Design philosophy
     * #4: facts derive from one source.
     *
     * History: this helper used to accept ``selectedValues`` and discard
     * it, always returning ``default_sale_price``. A shopper picking an
     * upcharged 2XL was therefore QUOTED the M price. Checkout was never
     * affected -- it has always been size-aware -- so the defect was a
     * wrong quote, not a wrong charge (Jake + Mike, 2026-08-26).
     *
     * @param {Object} campaignProduct - carries ``variant_prices``
     *   (server-resolved, ``{primary: {secondary: <cents>}}``, qty 1)
     *   and/or ``price_preview`` (server-resolved, ``{size: <cents>}``,
     *   qty 1). ``default_sale_price`` is NOT read (2026-09-29).
     * @param {Object} [selectedValues] - the shopper's picks, keyed by the
     *   product's OWN attribute names (``primary_attribute_name`` /
     *   ``secondary_attribute_name``), which vary per product. LEGACY --
     *   prefer ``variantValues``. This shape is UNSOUND on a product with
     *   no attribute names: see ``variantValues``.
     * @param {Object} [variantValues] - ``{primary, secondary}``, the
     *   shopper's picks as plain VALUES. Prefer this at every call site.
     *
     *   Why it exists: ``selectedValues`` is built by assigning INTO an
     *   object under the product's attribute names, and both names are
     *   NULL on most campaign products (58 of 96 on the 8083 sandbox on
     *   2026-08-28; 14 of those carry real per-size overrides). Two
     *   assignments keyed ``null`` and ``undefined`` both stringify to
     *   the SAME property, so
     *
     *       sv[cp.primary_attribute_name] = 'Dark Green'
     *       sv[cp.secondary_attribute_name] = '2XL'
     *
     *   yields ``{"null": "2XL"}`` -- one key, and the COLOUR IS GONE
     *   before this helper is called. That is why PR #2993 did not fix
     *   the reported bug and why no lookup strategy inside this helper
     *   can: the information is already destroyed. Passing the two values
     *   separately is the only sound fix.
     * @param {number} [unitPriceCents] - an already-resolved price for
     *   THIS line, e.g. from the ``prices`` array returned by the
     *   ``update_cart_quantity`` RPC. Wins over the product-level map: it
     *   was resolved at the line's real quantity and so accounts for
     *   quantity breaks the product-level map cannot express.
     * @returns {number|null} dollars, or null when the server has no
     *   price for this variant. Never a string: callers do arithmetic on
     *   a returned price (``price * quantity``, ``price.toFixed``), and a
     *   string would concatenate or throw. Every caller must handle null
     *   by showing "no price" and refusing to proceed -- there is no
     *   default to fall back on (2026-09-29).
     */
    computeProductVariantPrice (
      campaignProduct, selectedValues, unitPriceCents, variantValues,
    ) {
      const cp = campaignProduct || {}
      // 1. A price resolved for THIS line wins outright.
      if (centsAreUsable(unitPriceCents)) {
        return centsToDollars(unitPriceCents)
      }

      // 2. The server's per-variant map. Tried by NAME first, then by
      //    VALUE.
      //
      //    By name is the precise read: attribute names and their order
      //    vary per product, so guessing positionally would silently
      //    misprice any product that names or orders its attributes
      //    differently. It stays first so the products that carry names
      //    resolve exactly as they always have.
      //
      //    But ``primary_attribute_name`` / ``secondary_attribute_name``
      //    are NULL on most campaign products -- 58 of 96 on the 8083
      //    sandbox, 14 of them with real per-size overrides. For those,
      //    every call site builds ``{null: colour, undefined: size}`` and
      //    the name-keyed read misses, which is how an upcharged 2XL was
      //    still being quoted at ``default_sale_price`` after PR #2993.
      //    ``variant_prices`` is keyed by the VALUES themselves
      //    (colour -> size -> cents), so the values the shopper picked are
      //    a complete key on their own and the name indirection is not
      //    needed to read it.
      const map = cp.variant_prices
      const mapIsUsable = !!(map && typeof map === 'object')

      // 2a. VALUES PASSED EXPLICITLY. The only lookup that is safe when a
      //     product has no attribute names, and therefore the one every
      //     call site should use. See ``variantValues`` on the signature:
      //     with both names null the name-keyed object collapses to a
      //     SINGLE key and the colour is destroyed before it ever reaches
      //     this helper, so no amount of cleverness here can recover it.
      if (mapIsUsable && variantValues) {
        const cents = readVariantCents(
          map,
          variantValues.primary,
          variantValues.secondary,
        )
        if (cents !== undefined) {
          return centsToDollars(cents)
        }
      }

      // 2b. The legacy name-keyed read, for call sites still passing
      //     ``selectedValues``. Precise when the product HAS attribute
      //     names -- names and their order vary per product, so guessing
      //     positionally would misprice any product that names or orders
      //     its attributes differently -- and it keeps the 38 of 96
      //     name-carrying products on 8083 resolving exactly as they do
      //     today.
      if (mapIsUsable && selectedValues) {
        const cents = readVariantCents(
          map,
          selectedValues[cp.primary_attribute_name],
          selectedValues[cp.secondary_attribute_name],
        )
        if (cents !== undefined) {
          return centsToDollars(cents)
        }

        // 2c. Last resort for a name-keyed caller on a product with no
        //     names: match whatever distinct values the object still
        //     holds against the map's OWN keys. This RECOVERS the case
        //     where only ONE of the two names is null (the surviving
        //     object still carries both values under distinct keys); it
        //     cannot recover the both-null case, which is why 2a exists.
        //
        //     The map's structure decides which value is which -- a value
        //     is treated as the primary only if it is an actual top-level
        //     key -- rather than the caller's key order, which carries no
        //     information once the names are null. Only ONE combination is
        //     attempted per candidate primary, so a value that happens to
        //     be both a colour and a size cannot silently resolve to the
        //     transposed cell. (No such product exists today: zero of 96
        //     campaign products on 8083 share a value between
        //     ``selected_color_skus`` and ``selected_sizes``. This keeps it
        //     unambiguous if one ever does.)
        const picked = []
        for (const value of Object.values(selectedValues)) {
          if (value !== null && value !== undefined && value !== '') {
            if (!picked.includes(value)) {
              picked.push(value)
            }
          }
        }
        for (const primary of picked) {
          if (!Object.prototype.hasOwnProperty.call(map, primary)) {
            continue
          }
          for (const secondary of picked) {
            if (secondary === primary) {
              continue
            }
            const cents = readVariantCents(map, primary, secondary)
            if (cents !== undefined) {
              return centsToDollars(cents)
            }
          }
        }
      }

      // 3. The server's per-SIZE quote at quantity 1, ``price_preview``
      //    ({size: cents}), which the campaign-product serialization
      //    carries. It is what surfaces whose payload has no
      //    ``variant_prices`` -- the admin/portal order dialogs read
      //    ``campaign.campaign_products`` -- price from (2026-09-29).
      const preview = cp.price_preview
      if (preview && typeof preview === 'object') {
        const size = variantValues
          ? variantValues.secondary
          : selectedValues && cp.secondary_attribute_name != null
            ? selectedValues[cp.secondary_attribute_name]
            : undefined
        if (size != null) {
          const cents = preview[size]
          if (centsAreUsable(cents)) {
            return centsToDollars(cents)
          }
        }
      }

      // 4. No server price for this variant: return null.
      //
      //    There is deliberately NO fallback to ``default_sale_price``
      //    (removed 2026-09-29). That column is the old
      //    ``basis * (1 + default_margin)`` formula and the MIN size price,
      //    so it quoted every 2XL+ line low and disagreed with the charge.
      //    A null makes each caller show "no price" and refuse to proceed
      //    rather than invent a number.
      //
      //    A miss against a PRESENT ``variant_prices`` map is still LOUD:
      //    the server resolves a price for every offered variant, so a
      //    miss there almost always means this surface keyed the lookup
      //    wrongly (the PR #2993 failure). A product with no map at all is
      //    silent.
      if (mapIsUsable && (variantValues || selectedValues)) {
        // eslint-disable-next-line no-console
        console.warn(
          '[computeProductVariantPrice] no variant price for ' +
            `campaign_product=${cp.id != null ? cp.id : '?'} ` +
            `values=${JSON.stringify(variantValues || selectedValues)} -- ` +
            'returning no price. The server sent a variant_prices map ' +
            'that does not contain this combination, so this surface is ' +
            'probably keying the lookup wrongly.',
        )
      }
      return null
    },

    /**
     * @deprecated Use `salesPriceFormatNumber` from `@/utils/currency`
     * directly (non-mixin components, e.g. ProductsTableView.vue, already
     * do). Returns a bare formatted number ("1,234.56") for use inside
     * markup that supplies its own `$` prefix (`${{ salesPriceFormat(x) }}`).
     * Thin wrapper kept for the 7 existing mixin-based call sites so their
     * signature doesn't change; the "—" -> "0.00" fallback itself lives in
     * ONE place (salesPriceFormatNumber) rather than being duplicated here.
     */
    salesPriceFormat (price) {
      return salesPriceFormatNumber(price)
    },

    randomString (length) {
      var result = ''
      var characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
      var charactersLength = characters.length
      for (var i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength))
      }
      return result
    },

    numberWithCommas (number) {
      return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },

        // sets a color to black if the color is too bright
    setBlackIfTooLight (color) {
      if (!color) {
        return '#000000'
      }
      var c = color.substring(1) // strip #
      var rgb = parseInt(c, 16) // convert rrggbb to decimal
      var r = (rgb >> 16) & 0xff // extract red
      var g = (rgb >> 8) & 0xff // extract green
      var b = (rgb >> 0) & 0xff // extract blue

      var luma = 0.2126 * r + 0.7152 * g + 0.0722 * b // per ITU-R BT.709

      if (luma > 186) {
        return '#000000'
      } else {
        return color
      }
    },

    setWhiteIfTooDark (color) {
      var c = color.substring(1)      // strip #
      var rgb = parseInt(c, 16)   // convert rrggbb to decimal
      var r = (rgb >> 16) & 0xff  // extract red
      var g = (rgb >> 8) & 0xff  // extract green
      var b = (rgb >> 0) & 0xff  // extract blue

      var luma = 0.2126 * r + 0.7152 * g + 0.0722 * b // per ITU-R BT.709

      if (luma < 40) {
        return '#FFFFFF'
      } else {
        return color
      }
    },

    capitalizeFirstLetter (string) {
      if (string) {
        return string.charAt(0).toUpperCase() + string.slice(1)
      } else {
        return ''
      }
    },

    sleep (ms) {
      return new Promise((resolve) => setTimeout(resolve, ms))
    },

    lorem (index = 9999) {
      const lorem = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Volutpat maecenas volutpat blandit aliquam etiam erat velit scelerisque. Venenatis tellus in metus vulputate. Condimentum id venenatis a condimentum. Fringilla ut morbi tincidunt augue interdum. Nisl tincidunt eget nullam non nisi est. Habitant morbi tristique senectus et netus et malesuada fames. Accumsan tortor posuere ac ut consequat semper viverra. Maecenas volutpat blandit aliquam etiam erat velit scelerisque in. Adipiscing elit duis tristique sollicitudin nibh sit amet. Adipiscing vitae proin sagittis nisl rhoncus. Eu mi bibendum neque egestas congue quisque egestas diam in. Odio pellentesque diam volutpat commodo sed egestas egestas. Sem viverra aliquet eget sit amet tellus. Morbi tincidunt augue interdum velit euismod in pellentesque massa placerat. Eu scelerisque felis imperdiet proin fermentum leo vel. Ut morbi tincidunt augue interdum velit euismod in pellentesque. Id porta nibh venenatis cras sed felis eget velit aliquet. Pharetra sit amet aliquam id diam maecenas ultricies mi. Leo in vitae turpis massa sed elementum tempus egestas sed. Amet mattis vulputate enim nulla aliquet porttitor lacus luctus accumsan. Est velit egestas dui id ornare arcu odio ut sem. Lectus sit amet est placerat. Sed viverra ipsum nunc aliquet bibendum enim. Massa ultricies mi quis hendrerit dolor. Cras semper auctor neque vitae tempus. Vitae tortor condimentum lacinia quis vel eros donec. Nibh venenatis cras sed felis eget. Ultrices eros in cursus turpis massa tincidunt. Egestas egestas fringilla phasellus faucibus scelerisque. Egestas sed tempus urna et pharetra pharetra. Aliquet risus feugiat in ante metus dictum at tempor commodo. Sagittis eu volutpat odio facilisis mauris sit amet.'
      if (index >= lorem.length) {
        return lorem
      } else {
        return lorem.substring(0, index)
      }
    },

    getProcessingFeeInteger (amount) {
      return (((amount + 30) / (1 - 0.029)) - amount).toFixed(0)
    },

    randomColor () {
      var makeColorCode = '0123456789ABCDEF'
      var code = '#'
      for (var count = 0; count < 6; count++) {
        code = code + makeColorCode[Math.floor(Math.random() * 16)]
      }
      return code
    },

    calculateCartGrandTotal (
            cart,
            companyAllowance,
            fundraisingEnabled,
            fundraisingAmountPerItem,
            fundraisingType,
            taxEnabled,
            taxAmount,
            shippingEnabled,
            shippingAmount,
            couponType,
            couponAmount,
            collectProcessingFee,
            isCreditCardOrder) {
            /*
            Cart Charge Order Of Calculation
            1. Add up total cost of order items
            2. Add sales tax (if enabled)
            3. Apply coupon (if enabled)
            4. Apply shopper allowance (if enabled)
            5. Apply shipping (if enabled)
            6. Apply processing fee (if enabled)
            7. If credit card order and total is less than 50 cent, set total to 50 cent
            */

            // 1. Add up total cost of order items
      let total = this.getSubTotal(cart, fundraisingType, fundraisingAmountPerItem, fundraisingEnabled)

            // 2. Add sales tax (if enabled)
      if (taxEnabled) {
        total += taxAmount
      }

            // 3. Apply coupon (if enabled)
      if (couponAmount > 0) {
        total = this.applyCoupon(total, couponType, couponAmount)
      }

            // 4. Apply shopper allowance (if enabled)
      if (companyAllowance > 0) {
        total = this.applyShopperAllowance(total, companyAllowance)
      }

            // 5. Apply shipping (if enabled)
      if (shippingEnabled) {
        total += shippingAmount
      }

      if (total < 0) {
        total = 0
      }

            // 6. Apply processing fee (if enabled)
      if (total > 0 && collectProcessingFee && isCreditCardOrder) {
        total = this.applyProcessingFee(total)
      }

            // 7. If credit card order and total is less than between 0 and 50 cent, set total to 50 cent
      if (isCreditCardOrder) {
        total = this.apply50CentRule(total)
      }

      return total
    },

    apply50CentRule (subtotal) {
      if (subtotal < 0.5 && subtotal > 0) {
        return 0.5
      } else {
        return subtotal
      }
    },

    applyProcessingFee (subtotal) {
      const chargeBeforeFee = subtotal
      const newSubTotal = (chargeBeforeFee + 0.3) / (1 - 0.029)
      return newSubTotal
    },

    getProcessingFee (subtotal) {
      const chargeBeforeFee = subtotal
      const newSubTotal = (chargeBeforeFee + 0.3) / (1 - 0.029)

      const cartProcessingFee = newSubTotal - chargeBeforeFee
      return cartProcessingFee

            // (225.69 + .03) / .97
            // 232.7
    },

    // STALE BY DESIGN (#633): legacy recompute, not grossed up over the
    // platform fee. Only ResponsiveOrderView's fallback when the server
    // order totals fetch fails; the charge is composed by cartChargeCents.js.
    getProcessingFeeFromCart (
            cart,
            companyAllowance,
            fundraisingEnabled,
            fundraisingAmountPerItem,
            fundraisingType,
            taxEnabled,
            taxAmount,
            shippingEnabled,
            shippingAmount,
            couponType,
            couponAmount) {
            /*
            Cart Charge Order Of Calculation
            1. Add up total cost of order items
            2. Add sales tax (if enabled)
            3. Apply coupon (if enabled)
            4. Apply shopper allowance (if enabled)
            5. Apply shipping (if enabled)
            6. Apply processing fee (if enabled)
            7. If credit card order and total is less than 50 cent, set total to 50 cent
            */

            // 1. Add up total cost of order items
      let total = this.getSubTotal(cart, fundraisingType, fundraisingAmountPerItem, fundraisingEnabled)

            // 2. Add sales tax (if enabled)
      if (taxEnabled) {
        total += taxAmount
      }

            // 3. Apply coupon (if enabled)
      if (couponAmount > 0) {
        total = this.applyCoupon(total, couponType, couponAmount)
      }

            // 4. Apply shopper allowance (if enabled)
      if (companyAllowance > 0) {
        total = this.applyShopperAllowance(total, companyAllowance)
      }

            // 5. Apply shipping (if enabled)
      if (shippingEnabled) {
        total += shippingAmount
      }
      if (total < 0) {
        total = 0
      }
      const processingFee = this.getProcessingFee(total)

      return processingFee
    },

    applyShopperAllowance (subtotal, allowance) {
      let newSubTotal = 0
      newSubTotal = subtotal - allowance
      return newSubTotal
    },

    applyCoupon (subtotal, couponType, couponAmount) {
      let newSubTotal = subtotal
      if (couponAmount > 0) {
        if (couponType === '%') {
          newSubTotal = (subtotal * (100 - couponAmount)) / 100
        }
        if (couponType === '$') {
          newSubTotal = subtotal - couponAmount
        }
      }
      return newSubTotal
    },

    getSubTotal (cart, fundraisingType, fundraisingAmountPerItem, fundraisingEnabled) {
      let newSubTotal = 0
      cart.forEach((product) => {
        newSubTotal += this.getItemPrice(product, fundraisingType, fundraisingAmountPerItem, fundraisingEnabled) * product.quantity
      })
      return newSubTotal
    },

    getItemPrice (item, fundraisingType, fundraisingAmountPerItem, fundraisingEnabled) {
      let price = parseFloat(item.price.toFixed(2))
      if (fundraisingEnabled) {
        if (fundraisingType === 'percentage') {
          price = price / ((100 - fundraisingAmountPerItem) / 100)
        } else if (fundraisingType === 'flat_rate') {
          price += fundraisingAmountPerItem
        }
      }
      return price
    },

    locationToString (location) {
      const string =
                location.street_and_number +
                ', ' +
                (location.suite_unit_etc ? location.suite_unit_etc + ', ' : '') +
                location.city +
                ', ' +
                location.region +
                ', ' +
                location.zip_code +
                ', ' +
                location.country
      return string
    },

    stringLocationToObject (location) {
      const formattedLocation = {}
      try {
        const locationPieces = location.split(',')
        if (locationPieces.length > 5) {
          formattedLocation.street_and_number = locationPieces[0].trim()
          formattedLocation.suite_unit_etc = locationPieces[1].trim()
          formattedLocation.city = locationPieces[2].trim()
          formattedLocation.region = locationPieces[3].trim()
          formattedLocation.zip_code = locationPieces[4].trim()
          formattedLocation.country = locationPieces[5].trim()

          formattedLocation.isCompanyLocation = false
        } else {
          formattedLocation.street_and_number = locationPieces[0].trim()
          formattedLocation.suite_unit_etc = ''
          formattedLocation.city = locationPieces[1].trim()
          formattedLocation.region = locationPieces[2].trim()
          formattedLocation.zip_code = locationPieces[3].trim()
          formattedLocation.country = locationPieces[4].trim()
          formattedLocation.isCompanyLocation = false
        }
      } catch (err) {
        return null
      }
      return formattedLocation
    },

    truncateDate (date) {
      return date.split(' ')[0]
    },

    validateSpecificEmailDomain (email, domain) {
            // regular expression to match email address format
      var emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

            // check if email matches the format
      if (!emailRegex.test(email)) {
        return 'Domain format is incorrect'
      }

            // extract the domain name from the email address
      var emailDomain = email.split('@')[1]

            // check if domain name matches the specific domain
      if (emailDomain !== domain) {
        return 'Domain format is incorrect'
      }

      return true
    },

    getAppFeeMultiplierFromService (campaignId) {
      return axiosInstance.get(`/subscription/getAppFeeByCampaignAccount/${campaignId}`)
            .then(res => { return res.data })
            .catch(err => {
              console.log(describeError(err))
              return 0
            })
    },

    // ThreadFlow platform fee context FOR A GIVEN STORE: the resolved fee
    // rate (fraction, e.g. 0.0395) and who bears it. Read so the storefront
    // cart can fold the fee into the grand total the backend re-validates.
    //
    // STORE-SCOPED, NOT GLOBAL. This deliberately does NOT read
    // /platform-settings/platform-fee-rate: that endpoint answers the
    // SA-global default with no store or distributor context, so on a store
    // whose plan sets platform_fee_rate_override = 0 it returned 3.95% while
    // the server charged nothing, and checkout 409'd on the mismatch. The
    // slug-scoped route resolves through the same authority the checkout
    // math uses.
    //
    // NO RATE FALLBACK, BY DESIGN. This used to return 0.0395 when the read
    // failed. A guessed rate is wrong in BOTH directions -- on a zero-fee
    // store it silently converts a transient error into a 409 the shopper
    // cannot act on. The rejected value is thrown instead so the caller can
    // block the total and surface a real error rather than compute a
    // confidently wrong one.
    //
    // Rejects when `slug` is missing for the same reason: without a store
    // there is no store-scoped answer, and the global rate is not a
    // substitute for one.
    getPlatformFeeRateFromService (slug) {
      if (!slug) {
        return Promise.reject(
          new Error('getPlatformFeeRateFromService requires a store slug')
        )
      }
      return axiosInstance
            .get(`/storefront/${encodeURIComponent(slug)}/platform-fee-rate`)
            .then(res => {
              const v = res && res.data ? res.data.value : null
              if (v === null || v === undefined || v === '' || isNaN(v)) {
                throw new Error(
                  `platform fee rate unavailable for store "${slug}"`
                )
              }
              // `bearer` is advisory: the cart treats anything that is not
              // the exact string "distributor" as "shopper", mirroring the
              // backend resolve_platform_fee_bearer, so an absent or
              // unexpected value keeps today's default behaviour.
              return {
                rate: Number(v),
                bearer: (res.data && res.data.bearer) || 'shopper'
              }
            })
    },

    calculateApplicationFee (amount, multiplier) {
            // the stripe application fee allows order write to monetize each transaction
      const feeMultiplier = isNaN(multiplier) ? 0 : multiplier
      const fee = currency(amount).multiply(feeMultiplier)
      return fee
    },

    calculateCardProcessingFee (amount) {
      let cartProcessingFee = currency(0)
      const chargeBeforeFee = currency(amount)
      if (chargeBeforeFee.value > 0) {
                // fee should be (amount * 2.9%) + .30
        cartProcessingFee = chargeBeforeFee.multiply(0.029).add(0.30)
      }
      return cartProcessingFee
    }
  }
}
